import React, { useState, useRef, useEffect } from 'react';
import {
  Send, Paperclip, Image as ImageIcon, FileText,
  ShieldCheck, LayoutGrid, ListChecks, FileBox, AlertTriangle,
  Target, CheckCircle2, Activity, User, Clock, Plus, Zap, X, Download, MessageSquare,
  Globe, Server, Users, Database, Sparkles, BookOpen, Building, ArrowRight, BarChart3, Lock
} from 'lucide-react';
import ProfileTab from '../components/aiBuddy/artifacts/ProfileTab';
import FrameworksTab from '../components/aiBuddy/artifacts/FrameworksTab';
import ScopeTab from '../components/aiBuddy/artifacts/ScopeTab';
import RisksTab from '../components/aiBuddy/artifacts/RisksTab';
import ControlsTab from '../components/aiBuddy/artifacts/ControlsTab';
import EvidenceTab from '../components/aiBuddy/artifacts/EvidenceTab';
import EvaluationTab from '../components/aiBuddy/artifacts/EvaluationTab';
import GapsTab from '../components/aiBuddy/artifacts/GapsTab';
import RemediationTab from '../components/aiBuddy/artifacts/RemediationTab';
import ReportsTab from '../components/aiBuddy/artifacts/ReportsTab';
import { handleFetchRequest } from '../ApiHelper/ApiHelper';
import { apiUrl } from '../ApiHelper/ApiUrl';

const AiBuddy = () => {
  // --- CORE UI STATE ---
  const [activeArtifactTab, setActiveArtifactTab] = useState('Profile');
  const [hasStarted, setHasStarted] = useState(true); // Default to false to show start screen

  // --- DYNAMIC DATA STATES FOR ARTIFACT TABS ---
  const [profileData, setProfileData] = useState({
    industry: 'SaaS', regions: 'US, EU', dataSensitivity: 'Customer Data', infrastructure: 'AWS', companySize: '120 Employees'
  });

  const [frameworksData, setFrameworksData] = useState([
    { id: 1, name: 'SOC2 Type II', description: 'Systems and Organization Controls', reason: 'Handles customer data', controls: 60, color: 'indigo' },
    { id: 2, name: 'GDPR', description: 'General Data Protection Regulation', reason: 'Operates in EU', controls: 30, color: 'emerald' }
  ]);

  const [scopeData, setScopeData] = useState([
    { id: 1, system: 'Web Application', environment: 'Production', data: 'Customer Data', owner: 'Engineering' },
    { id: 2, system: 'Payment Gateway', environment: 'Production', data: 'Financial Data', owner: 'Finance' }
  ]);

  const [risksData, setRisksData] = useState([
    { id: 'R101', system: 'Web Application', title: 'Unauthorized access to admin panel', severity: 'High', likelihood: 'Medium', impact: 'High', status: 'Open', color: 'rose' }
  ]);

  const [controlsData, setControlsData] = useState([
    { id: 'CC6.1', framework: 'SOC2', category: 'Access Control', title: 'Logical Access', owner: 'DevOps Team', parameter: 'Access review every quarter', frequency: 'Quarterly', status: 'Passing' },
    { id: 'CC7.2', framework: 'SOC2', category: 'Monitoring', title: 'Incident Logging', owner: 'SecOps', parameter: 'Log review weekly', frequency: 'Weekly', status: 'Failing' }
  ]);

  const [evidenceData, setEvidenceData] = useState([
    { id: 1, control: 'CC6.1', required: 'Access Review Matrix', name: 'access_review.xlsx', uploadedBy: 'Coordinator', date: 'Mar 20', color: 'indigo' },
    { id: 2, control: 'CC7.2', required: 'Incident Log Report', name: 'incident_logs_Q1.pdf', uploadedBy: 'SecOps Lead', date: 'Mar 22', color: 'blue' }
  ]);

  const [evaluationData, setEvaluationData] = useState([
    { id: 1, control: 'CC6.1', score: 82, confidence: 'Medium', finding: 'Missing approval column', color: 'amber' }
  ]);

  const [gapsData, setGapsData] = useState([
    { id: "G201", control: "CC6.1", title: "Access review missing", description: "The uploaded access review matrix is missing the approval column for Q1.", severity: "High", status: "Open" }
  ]);

  const [remediationData, setRemediationData] = useState([
    { id: 'R301', gapId: 'G201', control: 'CC6.1', action: 'Implement quarterly access review', owner: 'DevOps', due: 'Apr 10', status: 'In Progress', color: 'amber' }
  ]);

  const [reportsData, setReportsData] = useState({
    controlsPassed: 45, controlsFailed: 3, openGaps: 2, complianceScore: 87,
    downloads: [
      { title: 'Audit Package', format: 'ZIP', date: 'Today, 9:00 AM', size: '12.4 MB' },
      { title: 'Executive Summary', format: 'PDF', date: 'Today, 9:05 AM', size: '2.1 MB' },
      { title: 'Control Matrix', format: 'Excel', date: 'Yesterday', size: '4.8 MB' }
    ]
  });

  const [interviewData, setInterviewData] = useState(null);

  // --- CHATBOT STATE ---
  const [messages, setMessages] = useState([]); // Default empty
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isAttachMenuOpen, setIsAttachMenuOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);

  // Smart Suggestions & Context State
  const defaultSuggestions = [
    "Add GDPR Framework",
    "Create a new control",
    "Log a security Risk",
    "Show me compliance gaps"
  ];
  const [suggestions, setSuggestions] = useState(defaultSuggestions);
  const [chatContext, setChatContext] = useState({ active: false, intent: null });

  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const artifactTabs = [
    { id: 'Profile', icon: Building }, { id: 'Frameworks', icon: LayoutGrid },
    { id: 'Scope', icon: Target }, { id: 'Risks', icon: Activity },
    { id: 'Controls', icon: ListChecks }, { id: 'Evidence', icon: FileBox },
    { id: 'Evaluation', icon: ShieldCheck }, { id: 'Gaps', icon: AlertTriangle },
    { id: 'Remediation', icon: CheckCircle2 }, { id: 'Reports', icon: BarChart3 }
  ];

  // --- HANDLERS ---
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping, pendingFiles, suggestions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.attach-menu-container')) setIsAttachMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    const newFiles = files.map(file => ({
      id: Date.now() + Math.random(), name: file.name, type: file.type, url: URL.createObjectURL(file)
    }));
    setPendingFiles(prev => [...prev, ...newFiles]);
    setIsAttachMenuOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (id) => setPendingFiles(prev => prev.filter(f => f.id !== id));

  const handleSendMessage = async (e, overrideText = null) => {
    e?.preventDefault();
    const text = overrideText || inputValue;
    if (!text.trim() && pendingFiles.length === 0) return;
  
    if (!hasStarted) setHasStarted(true);

    const newUserMsg = { id: Date.now(), role: 'user', content: text, files: pendingFiles };
    setMessages(prev => [...prev, newUserMsg]);
    
    setIsTyping(true);

    try {
      const payload = {
        user_prompt: text,
        project_id: localStorage.getItem('selectedProject'),
        framework: "SOC2",
        session_id: interviewData?.sessionId || "",
      }
      setInputValue("");
      const response = await handleFetchRequest("itgc", `${apiUrl.arc_ai_chat}/agent`, "POST", payload);

      // Update interview state
      setInterviewData({
        currentStage: response?.current_stage,
        currentSubstage: response?.current_substage,
        framework: response?.framework,
        sessionId: response?.session_id,
      });

      
      setIsTyping(false);
      // Add AI message
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: "ai",
          content: response?.response_msg || "No response",
        },
      ]);

      setInputValue("");

    } catch (error) {
      console.error("##API Error:", error);
      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: "ai",
          content: "Something went wrong.",
        },
      ]);
      
    }
  }


  // --- AI LOGIC (Dynamic Flow) ---
  const handleSendMessage1 = (e, overrideText = null) => {
    e?.preventDefault();
    const textToSend = overrideText || inputValue;
    if (!textToSend.trim() && pendingFiles.length === 0) return;

    if (!hasStarted) setHasStarted(true);

    const newUserMsg = { id: Date.now(), role: 'user', content: textToSend, files: pendingFiles };
    setMessages(prev => [...prev, newUserMsg]);
    setInputValue('');
    setPendingFiles([]);
    setSuggestions([]);
    setIsTyping(true);

    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    setTimeout(() => {
      let aiResponse = "";
      let newSuggestions = defaultSuggestions;
      const lowerInput = textToSend.toLowerCase();

      // Handling follow-up contexts
      if (chatContext.active) {
        if (chatContext.intent === 'add_framework') {
          const newFw = { id: Date.now(), name: textToSend, description: 'Added via chat', reason: 'User requested', controls: 0, color: 'emerald' };
          setFrameworksData(prev => [newFw, ...prev]);
          setActiveArtifactTab('Frameworks');
          aiResponse = `Perfect! I've added the framework **"${textToSend}"**. I've switched your view so you can see it in the Frameworks tab.`;
        }
        else if (chatContext.intent === 'add_control') {
          const newCtrl = { id: `CC${Math.floor(Math.random() * 10)}.${Math.floor(Math.random() * 10)}`, framework: 'Custom', category: 'General', title: textToSend, owner: 'Unassigned', parameter: 'TBD', frequency: 'TBD', status: 'Passing' };
          setControlsData(prev => [newCtrl, ...prev]);
          setActiveArtifactTab('Controls');
          aiResponse = `Control created! **"${textToSend}"** is now tracked in your Controls tab.`;
        }
        else if (chatContext.intent === 'add_risk') {
          const newRisk = { id: `R${Math.floor(Math.random() * 900) + 100}`, system: 'General', title: textToSend, severity: 'High', likelihood: 'Medium', impact: 'High', status: 'Open', color: 'rose' };
          setRisksData(prev => [newRisk, ...prev]);
          setActiveArtifactTab('Risks');
          aiResponse = `High-level Risk logged. **"${textToSend}"** has been securely added to the Risk Register.`;
        }
        setChatContext({ active: false, intent: null });
      }
      else {
        // Regex for direct commands
        const fwMatch = textToSend.match(/(?:add|create)\s+.*(?:framework)\s+(.+)/i);
        const ctrlMatch = textToSend.match(/(?:add|create)\s+.*(?:control)\s+(.+)/i);

        if (newUserMsg.files && newUserMsg.files.length > 0) {
          const newEvidences = newUserMsg.files.map(f => ({
            id: f.id, control: 'Unmapped', required: 'User Upload', name: f.name, uploadedBy: 'Current User', date: 'Just now', color: 'indigo'
          }));
          setEvidenceData(prev => [...newEvidences, ...prev]);
          setActiveArtifactTab('Evidence');
          aiResponse = `I received your ${newUserMsg.files.length} file(s). They are now ingested into your Evidence Repository!`;
        }
        else if (fwMatch) {
          const fwName = fwMatch[1].trim();
          setFrameworksData(prev => [{ id: Date.now(), name: fwName, description: 'Direct command', reason: 'User requested', controls: 0, color: 'emerald' }, ...prev]);
          setActiveArtifactTab('Frameworks');
          aiResponse = `I've directly added the **"${fwName}"** framework for you. Check it out in the Frameworks tab!`;
        }
        else if (ctrlMatch) {
          const ctrlName = ctrlMatch[1].trim();
          setControlsData(prev => [{ id: `NEW.${Math.floor(Math.random() * 10)}`, framework: 'Custom', category: 'General', title: ctrlName, owner: 'Unassigned', parameter: 'TBD', frequency: 'TBD', status: 'Passing' }, ...prev]);
          setActiveArtifactTab('Controls');
          aiResponse = `I've created the control **"${ctrlName}"**. You can assign an owner and parameters in the Controls tab.`;
        }
        else if (lowerInput.includes('framework')) {
          setChatContext({ active: true, intent: 'add_framework' });
          aiResponse = "I can help you create a new Framework. What is the official name of the framework you want to add?";
          newSuggestions = ["NIST CSF", "PCI-DSS", "Cancel"];
        }
        else if (lowerInput.includes('control')) {
          setChatContext({ active: true, intent: 'add_control' });
          aiResponse = "Let's map a new Control. Please provide a brief title or description for it.";
          newSuggestions = ["Require MFA for Admins", "Quarterly Access Reviews", "Cancel"];
        }
        else if (lowerInput.includes('risk')) {
          setChatContext({ active: true, intent: 'add_risk' });
          aiResponse = "Got it. Let's add a new Risk. What should we name this risk?";
          newSuggestions = ["Unpatched Servers", "Vendor Data Breach", "Cancel"];
        }
        else if (lowerInput.includes('gap')) {
          const newGap = { id: `G${Math.floor(Math.random() * 900) + 100}`, control: "AI-Gen", title: "AI Detected Anomaly", description: "Flagged dynamically from conversation context.", severity: "Medium", status: "Open" };
          setGapsData(prev => [newGap, ...prev]);
          setActiveArtifactTab('Gaps');
          aiResponse = "Based on our compliance data, I've flagged a new AI evaluated gap. I've switched your view to the Gaps tab so you can review it.";
        }
        else if (lowerInput === 'cancel') {
          setChatContext({ active: false, intent: null });
          aiResponse = "Action cancelled. What would you like to do instead?";
        }
        else if (lowerInput.includes('define') || lowerInput.includes('saas')) {
          // Provide a nice default response for the example prompt
          aiResponse = "I've processed your context. Based on your setup, I have generated your **Compliance Profile**. I highly recommend adding **SOC2 Type II** and **GDPR** frameworks to your scope.";
          setActiveArtifactTab('Profile');
        }
        else {
          aiResponse = "I'm tracking. You can ask me to **'Add a framework'**, **'Create a control'**, **'Log a risk'**, or just **upload a file** to see the tabs update dynamically!";
        }
      }

      setMessages(prev => [...prev, { id: Date.now() + 1, role: 'ai', content: aiResponse }]);
      setSuggestions(newSuggestions);
      setIsTyping(false);
    }, 1200);
  };

  const handleTextareaResize = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  const handleTabClick = (tabId, event) => {
    setActiveArtifactTab(tabId);
    if (!hasStarted) setHasStarted(true);

    event.currentTarget.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest'
    });
  };

  // --- RIGHT PANEL CONTENT RENDERING (UPDATED UI/UX) ---
  const renderArtifactContent = () => {
    if (!hasStarted) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center text-center h-full animate-in fade-in duration-700">
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-indigo-200 blur-3xl opacity-20 rounded-full"></div>
            <BookOpen size={56} strokeWidth={1} className="text-slate-300 relative z-10" />
          </div>
          <h3 className="text-xl font-semibold text-slate-800 mb-3">Workspace Initialization</h3>
          <p className="text-base text-slate-500 max-w-md leading-relaxed">
            Start by describing your architecture or compliance goals in the chat. The AI will dynamically generate your profile, controls, and risk matrix here.
          </p>
        </div>
      );
    }

    const TAB_COMPONENTS = {
      Profile: <ProfileTab profileData={profileData} />,
      Frameworks: <FrameworksTab frameworksData={frameworksData} />,
      Scope: <ScopeTab scopeData={scopeData} />,
      Risks: <RisksTab risksData={risksData} />,
      Controls: <ControlsTab controlsData={controlsData} />,
      Evidence: <EvidenceTab evidenceData={evidenceData} />,
      Evaluation: <EvaluationTab evaluationData={evaluationData} />,
      Gaps: <GapsTab gapsData={gapsData} />,
      Remediation: <RemediationTab remediationData={remediationData} />,
      Reports: <ReportsTab reportsData={reportsData} />,
    };

    return TAB_COMPONENTS[activeArtifactTab] || null;
  };

  return (
    <div className="flex h-screen w-full bg-slate-50 text-slate-900 overflow-hidden h-100">

      {/* --- LEFT PANEL --- */}
      <section className="w-[40%] shrink-0 flex flex-col bg-white border-r border-slate-200 shadow-[2px_0_10px_rgba(0,0,0,0.02)] relative z-10">

        <div className="p-6 flex items-center gap-3 border-b border-slate-100 shrink-0 h-[72px]">
          <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
            <Sparkles size={18} strokeWidth={2.5} />
          </div>
          <h2 className="text-sm font-bold tracking-wide text-slate-800">Arc AI</h2>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar relative p-6">
          <div className="space-y-6 h-full">

            {/* --- START SCREEN --- */}
            {messages.length === 0 && !isTyping ? (
              <div className="flex flex-col items-center pt-10  max-w-md mx-auto text-center mt-[-40px] animate-in fade-in duration-500">
                <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-6 ring-1 ring-indigo-100/50 shadow-sm">
                  <ShieldCheck size={32} strokeWidth={2} />
                </div>

                <h1 className="text-2xl font-bold text-slate-900 mb-4 tracking-tight">AI Compliance Generator</h1>

                <p className="text-[15px] text-slate-500 leading-relaxed mb-10 px-2 font-medium">
                  Describe your compliance goals below. The AI will create a full scope, generate controls, add risks, and compile your audit reports.
                </p>

                <div className="w-full text-left">
                  <p className="text-[11px] font-bold tracking-widest text-slate-400 uppercase mb-3 px-1">Try an example</p>
                  <div className="space-y-3">
                    {[
                      "\"Define a compliance profile for a SaaS company...\"",
                      "\"Add GDPR framework to my current scope...\"",
                      "\"Log a high-severity security risk for unpatched servers...\""
                    ].map((text, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSendMessage(null, text.replace(/"/g, ''))}
                        className="w-full text-left p-4 rounded-2xl bg-white border border-slate-200 hover:border-indigo-300 hover:ring-2 hover:ring-indigo-50 transition-all text-sm font-semibold text-slate-700 shadow-sm"
                      >
                        {text}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              /* --- ACTIVE CHAT UI --- */
              <>
                {messages.map((msg) => (
                  <div key={msg.id} className={`flex w-full ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-2`}>
                    <div className={`flex gap-3 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                      <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center shadow-sm mt-1 ${msg.role === 'ai' ? 'bg-gradient-to-br from-indigo-100 to-blue-50 text-indigo-600 ring-1 ring-indigo-200/60' : 'bg-slate-100 text-slate-600 ring-1 ring-slate-200'}`}>
                        {msg.role === 'ai' ? <Zap size={14} className="fill-indigo-100" /> : <User size={14} />}
                      </div>

                      <div className={`px-5 py-4 rounded-[1.5rem] text-sm leading-relaxed shadow-sm ${msg.role === 'user' ? 'bg-indigo-600 text-white rounded-tr-sm font-medium' : 'bg-white text-slate-700 ring-1 ring-slate-200/60 rounded-tl-sm font-medium'}`}>

                        {msg.files && msg.files.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {msg.files.map(file => (
                              <div key={file.id} className={`relative w-20 h-20 rounded-xl overflow-hidden ring-1 flex flex-col items-center justify-center ${msg.role === 'user' ? 'bg-white/20 ring-white/30' : 'bg-slate-50 ring-slate-200'}`}>
                                {file.type.startsWith('image/') ? (
                                  <img src={file.url} alt="attachment" className="w-full h-full object-cover" />
                                ) : (
                                  <>
                                    <FileText size={24} className={msg.role === 'user' ? 'text-white' : 'text-slate-400'} />
                                    <span className={`text-[8px] font-bold truncate px-2 w-full text-center mt-1.5 ${msg.role === 'user' ? 'text-white' : 'text-slate-500'}`}>{file.name}</span>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        <div dangerouslySetInnerHTML={{ __html: msg.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></div>
                      </div>
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="flex w-full justify-start animate-in fade-in">
                    <div className="flex gap-3 max-w-[80%]">
                      <div className="shrink-0 w-8 h-8 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center mt-1">
                        <Zap size={14} className="text-indigo-600" />
                      </div>
                      <div className="px-4 py-3 bg-white border border-slate-200 rounded-2xl rounded-tl-sm shadow-sm flex items-center gap-1.5 h-[44px]">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-pulse"></span>
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-pulse delay-75"></span>
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-pulse delay-150"></span>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
            <div ref={chatEndRef} />
          </div>
        </div>

        <div className="p-4 bg-white z-20 shadow-[0_-10px_40px_-15px_rgba(0,0,0,0.05)] border-t border-slate-100">

          {/* --- SUGGESTIONS CHIPS (ONLY SHOW IF CHAT HAS STARTED) --- */}
          {!isTyping && messages.length > 0 && suggestions.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {suggestions.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSendMessage(null, s)}
                  className="flex items-center gap-1.5 text-[11px] font-bold bg-indigo-50/80 text-indigo-700 hover:bg-indigo-600 hover:text-white px-3 py-1.5 rounded-full ring-1 ring-indigo-200/50 transition-all"
                >
                  <MessageSquare size={12} /> {s}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={handleSendMessage} className="flex flex-col bg-white ring-1 ring-slate-200 rounded-3xl p-2 shadow-sm focus-within:ring-indigo-500 focus-within:shadow-md transition-all relative">
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 px-3 pt-2 pb-2 border-b border-slate-100 mb-1">
                {pendingFiles.map(file => (
                  <div key={file.id} className="relative group w-14 h-14 rounded-xl overflow-hidden ring-1 ring-slate-200 bg-slate-50 flex flex-col items-center justify-center shrink-0">
                    {file.type.startsWith('image/') ? (
                      <img src={file.url} className="w-full h-full object-cover" alt="preview" />
                    ) : (
                      <>
                        <FileText className="text-slate-400" size={20} />
                        <span className="text-[8px] font-bold text-slate-500 truncate w-12 text-center mt-1">{file.name}</span>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => removePendingFile(file.id)}
                      className="absolute top-1 right-1 bg-black/60 backdrop-blur-md text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-all hover:bg-rose-500"
                    >
                      <X size={10} strokeWidth={3} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-col w-full">
              
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => { setInputValue(e.target.value); handleTextareaResize(e); }}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(e); } }}
                placeholder="Ask me to map frameworks, create controls..."
                rows={1}
                className="flex-1 bg-transparent resize-none outline-none py-3 text-sm font-medium text-slate-800 placeholder:text-slate-400 min-h-[44px] max-h-[240px] custom-scrollbar overflow-y-auto"
              />
              <div className="flex justify-between items-center px-2 pb-1 pl-0">

                <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" multiple />
                <div className="relative pb-1 pl-1 attach-menu-container">
                  <button type="button" onClick={() => setIsAttachMenuOpen(!isAttachMenuOpen)} className={`p-2.5 rounded-full transition-all ${isAttachMenuOpen ? 'bg-indigo-600 text-white shadow-md rotate-45' : 'text-slate-400 bg-indigo-50 hover:text-indigo-600 hover:bg-indigo-50'}`}>
                    <Plus size={20} strokeWidth={2.5} className="transition-transform duration-300" />
                  </button>
                  {isAttachMenuOpen && (
                    <div className="absolute bottom-[calc(100%+12px)] left-0 w-48 bg-white/95 backdrop-blur-xl rounded-2xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.2)] ring-1 ring-slate-200/80 p-2 z-50 animate-in fade-in slide-in-from-bottom-2 duration-200">
                      <button type="button" onClick={() => { fileInputRef.current?.click(); setIsAttachMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-slate-600 hover:text-indigo-600 hover:bg-slate-50 rounded-xl transition-colors"><FileText size={16} /> Document</button>
                      <button type="button" onClick={() => { fileInputRef.current?.click(); setIsAttachMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-slate-600 hover:text-indigo-600 hover:bg-slate-50 rounded-xl transition-colors"><ImageIcon size={16} /> Image File</button>
                      <button type="button" onClick={() => { fileInputRef.current?.click(); setIsAttachMenuOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-slate-600 hover:text-indigo-600 hover:bg-slate-50 rounded-xl transition-colors"><Paperclip size={16} /> Other File</button>
                    </div>
                  )}
                </div>

                <button type="submit" disabled={!inputValue.trim() && pendingFiles.length === 0} className="p-2.5 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-all shadow-md disabled:opacity-50 disabled:shadow-none disabled:bg-slate-100 disabled:text-slate-400 flex items-center justify-center group">
                  <Send size={18} strokeWidth={2.5} className="ml-0.5" />
                </button>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* --- RIGHT PANEL --- */}
      <section className="flex-1 flex flex-col bg-slate-50 relative overflow-hidden">

        <div className="flex items-center gap-2 px-6 h-[72px] border-b border-slate-200 bg-white shrink-0 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {artifactTabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeArtifactTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={(e) => handleTabClick(tab.id, e)}
                className={`flex items-center gap-2 px-2 py-2 text-sm font-bold whitespace-nowrap transition-all rounded-lg shrink-0 ${isActive ? 'bg-indigo-50 text-indigo-700' : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'}`}
              >
                <Icon size={16} className={isActive ? 'text-indigo-600' : 'text-slate-400'} /> {tab.id}
              </button>
            );
          })}
        </div>

        <div className="flex-1 p-8 overflow-y-auto custom-scrollbar">
          <div className="max-w-4xl mx-auto h-full flex flex-col">
            {renderArtifactContent()}
          </div>
        </div>

      </section>

    </div>
  );
};

export default AiBuddy;